import { useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Divider } from '~/components/ui/Divider';
import { InputRow } from '~/components/ui/InputRow';
import { MoneyInput } from '~/components/ui/MoneyInput';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction } from '~/rage';
import {
	Currency,
	IModalReplyWithValue,
	IModalStateBusinessChangePrice,
	InputAction,
} from '~/shared';
import { CurrencyIcon } from '../../CurrencyIcon';
import { ModalButton } from '../ModalButton';
import {
	StyledButtonsContainer,
	StyledDescription,
	StyledInputs,
	StyledModalBody,
} from './ModalBusinessChangePrice.styled';

const ModalBusinessChangePrice: React.FC<{
	state: IModalStateBusinessChangePrice;
}> = ({ state }) => {
	const currencyIcon = useWalletIcon(Currency.Ruble, true);

	const currentPrice = state?.currentPrice ?? 0;
	const maxPrice = state?.maxPrice ?? 0;
	const minPrice = state?.minPrice ?? 1;
	const unit = state?.unit;

	const [newPrice, setNewPrice] = useState<number>(0);

	const isValid = useMemo(
		() =>
			newPrice > 0 &&
			newPrice >= minPrice &&
			newPrice <= maxPrice &&
			newPrice !== currentPrice,
		[newPrice, minPrice, maxPrice, currentPrice],
	);

	const reply = (isAccepted: boolean) => {
		rage.modal.replyToCurrentModal({
			isAccepted,
			value: isAccepted ? newPrice : 0,
		} as IModalReplyWithValue<number>);
	};

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (isValid) {
			reply(true);
		}
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		reply(false);
	});

	const renderCurrencyIcon = (size: 'sm' | 'md' | 'lg' = 'sm') => (
		<CurrencyIcon icon={currencyIcon} size={size} style={{ opacity: 0.5 }} />
	);

	return (
		<StyledModalBody>
			{state?.text && <StyledDescription>{state.text}</StyledDescription>}

			<StyledInputs>
				<MoneyInput
					label="Новая цена"
					value={newPrice}
					onChange={setNewPrice}
					icon={renderCurrencyIcon('sm')}
					unit={unit}
					max={maxPrice}
					min={0}
					height={50}
					fontSize={16}
					padding="0 20px"
				/>
				<MoneyInput
					label="Текущая цена"
					value={currentPrice}
					onChange={() => undefined}
					icon={renderCurrencyIcon('sm')}
					unit={unit}
					disabled
					height={50}
					fontSize={16}
					padding="0 20px"
				/>
			</StyledInputs>

			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			<InputRow label={'Макс. цена за 1 ед.'} icon={renderCurrencyIcon('md')}>
				<Text>{maxPrice.toLocaleString('ru-RU')}</Text>
			</InputRow>

			<StyledButtonsContainer>
				<ModalButton
					$type={state?.cancelButton.color}
					onClick={() => {
						reply(false);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.cancelButton.text}
				</ModalButton>
				<ModalButton
					$type={state?.acceptButton.color}
					disabled={!isValid}
					onClick={() => {
						if (!isValid) {
							return;
						}
						reply(true);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.acceptButton.text}
				</ModalButton>
			</StyledButtonsContainer>
		</StyledModalBody>
	);
};

export { ModalBusinessChangePrice };
